import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { useBookmarkContext } from "../hooks/useBookmarkContext";
import {
  exportCardAsImage,
  exportCardAsBlob,
  generateCardFilename,
} from "../utils/cardExport";
import { getPaletteForSection } from "../utils/colorPalettes";
import { logError } from "../utils/logger";
import type {
  NewNarrativeData,
  DataValues,
  BookmarkData,
} from "../types/narrative";

interface HighlightsGalleryProps {
  narrativeData: NewNarrativeData;
  data: DataValues;
  showAllCards?: boolean; // Not used anymore - kept for API compatibility
  onComplete?: () => void;
}

interface BookmarkReference {
  groupId: string;
  bookmarkData: BookmarkData;
}

// CSS overrides to force cloned content to expand to full width
// This removes the narrow phone-frame constraints from the original DOM
const snapshotStyleOverrides = `
  .bookmark-snapshot-content > div {
    max-width: none !important;
    width: 100% !important;
  }
  .bookmark-snapshot-content * {
    max-width: none !important;
  }
`;

// Base dimensions for the snapshot content layout
// Content reflows to this width, then gets scaled to fit target size
const SNAPSHOT_BASE_WIDTH = 500;
const SNAPSHOT_BASE_HEIGHT = 500;

// Component to render a bookmark snapshot with gradient background
function BookmarkSnapshot({
  bookmarkData,
  className = "",
  size = 300,
}: {
  bookmarkData: BookmarkData;
  className?: string;
  size?: number; // Square size in pixels (1:1 aspect ratio)
}) {
  // Get section palette for background gradient (supports inline palette stored in bookmark)
  const palette = getPaletteForSection(
    bookmarkData.sectionId,
    bookmarkData.colorPalette,
  );

  // Scale factor to fit base dimensions into target size
  const scale = size / SNAPSHOT_BASE_WIDTH;

  return (
    <div
      className={`relative overflow-hidden rounded-xl ${className}`}
      style={{
        // Square container
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      {/* Inject style overrides */}
      <style>{snapshotStyleOverrides}</style>

      {/* Gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, ${palette.from} 0%, ${palette.via} 50%, ${palette.from} 100%)`,
        }}
      />

      {/* Scaled content container */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        <div
          style={{
            width: `${SNAPSHOT_BASE_WIDTH}px`,
            height: `${SNAPSHOT_BASE_HEIGHT}px`,
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
          }}
        >
          {/* Content wrapper with class for CSS targeting */}
          <div
            className="bookmark-snapshot-content w-full"
            dangerouslySetInnerHTML={{ __html: bookmarkData.snapshot }}
          />
        </div>
      </div>
    </div>
  );
}

export function HighlightsGallery({ narrativeData }: HighlightsGalleryProps) {
  const { bookmarks, setBookmark } = useBookmarkContext();
  const [selectedBookmark, setSelectedBookmark] =
    useState<BookmarkReference | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const exportCardRef = useRef<HTMLDivElement>(null);

  // Collect all bookmarked items
  const bookmarkedItems: BookmarkReference[] = Object.entries(bookmarks)
    .filter(([, data]) => data !== null)
    .map(([groupId, data]) => ({
      groupId,
      bookmarkData: data as BookmarkData,
    }));

  const handleCardClick = (bookmark: BookmarkReference) => {
    setSelectedBookmark(bookmark);
  };

  const handleCloseModal = () => {
    setSelectedBookmark(null);
  };

  const handleRemoveBookmark = () => {
    if (selectedBookmark) {
      setBookmark(selectedBookmark.groupId, null);
      setSelectedBookmark(null);
    }
  };

  const handleDownload = async () => {
    if (!exportCardRef.current || !selectedBookmark) return;

    setIsExporting(true);
    try {
      const filename = generateCardFilename(
        narrativeData.metadata.customer_name,
        selectedBookmark.bookmarkData.sectionId,
        narrativeData.metadata.timestamp_generated,
      );

      // Detect iOS - use Web Share API for better Photos integration
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS && navigator.share && navigator.canShare) {
        // On iOS, use share sheet which includes "Save Image" (saves to Photos)
        const blob = await exportCardAsBlob(exportCardRef.current);
        const file = new File([blob], filename, { type: "image/png" });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Save Insight Card",
          });
          setIsExporting(false);
          return;
        }
      }

      // Standard download for desktop and non-iOS
      await exportCardAsImage(exportCardRef.current, filename);
    } catch (error: unknown) {
      // User cancelled share sheet
      if (error instanceof Error && error.name === "AbortError") {
        setIsExporting(false);
        return;
      }
      logError("Export failed:", error);
      alert("Failed to download card. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = async () => {
    if (!selectedBookmark || !exportCardRef.current) return;

    // Check if Web Share API is available and supports files (mobile/modern browsers)
    if (navigator.share && navigator.canShare) {
      setIsExporting(true);
      try {
        const blob = await exportCardAsBlob(exportCardRef.current);
        const filename = generateCardFilename(
          narrativeData.metadata.customer_name,
          selectedBookmark.bookmarkData.sectionId,
          narrativeData.metadata.timestamp_generated,
        );
        const file = new File([blob], filename, { type: "image/png" });

        // Check if we can share this file
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Insight Snapshot",
          });
          setIsExporting(false);
          return;
        }
      } catch (error: unknown) {
        setIsExporting(false);
        // User cancelled or share failed
        if (error instanceof Error && error.name === "AbortError") {
          return; // User cancelled, don't fallback
        }
      }
    }
  };

  // Empty state when no bookmarks
  if (bookmarkedItems.length === 0) {
    return (
      <div className="w-full space-y-8">
        {/* Header */}
        <div className="flex items-center justify-center">
          <h2
            className="text-3xl text-white"
            style={{
              fontFamily: "var(--font-narrative-header)",
              fontWeight:
                "var(--font-narrative-header-weight, 700)" as unknown as number,
            }}
          >
            Your Bookmarks
          </h2>
        </div>

        <div className="flex justify-center">
          <div className="max-w-md text-center space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="text-6xl">📚</div>
              <h3 className="text-2xl font-semibold text-white">
                No bookmarks yet
              </h3>
              <p className="text-white/70">
                Bookmark insights throughout the presentation to build your
                collection.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      {/* Header */}
      <div className="flex items-center justify-center">
        <h2
          className="text-3xl text-white"
          style={{
            fontFamily: "var(--font-narrative-header)",
            fontWeight:
              "var(--font-narrative-header-weight, 700)" as unknown as number,
          }}
        >
          Your Bookmarks
        </h2>
      </div>

      {/* Horizontal scrolling card grid - centered when few items, scrollable when many */}
      <div className="relative -mx-8">
        <div
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 px-8 scrollbar-hide justify-center"
          style={{
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {bookmarkedItems.map((bookmark, index) => (
            <motion.div
              key={bookmark.groupId}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex-shrink-0 snap-center cursor-pointer"
              onClick={() => handleCardClick(bookmark)}
              style={{ scrollSnapAlign: "center" }}
            >
              <BookmarkSnapshot
                bookmarkData={bookmark.bookmarkData}
                size={280}
                className="hover:scale-105 transition-transform duration-200"
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Card count */}
      <p className="text-center text-white/60 text-sm">
        {bookmarkedItems.length}{" "}
        {bookmarkedItems.length === 1 ? "bookmark" : "bookmarks"}
      </p>

      {/* Fullscreen Modal - Use portal to render at document root */}
      {selectedBookmark &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center"
              style={{
                background: "rgba(0, 0, 0, 0.7)",
                backdropFilter: "blur(8px)",
              }}
              onClick={handleCloseModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative max-w-2xl w-full space-y-6 p-4"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={handleCloseModal}
                  className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-colors"
                  aria-label="Close"
                >
                  <Icon icon="xmark" className="text-xl" />
                </button>

                {/* Card display - larger preview */}
                <div className="flex justify-center">
                  <BookmarkSnapshot
                    bookmarkData={selectedBookmark.bookmarkData}
                    size={400}
                    className="shadow-2xl"
                  />
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-4">
                  {/* Download and Share buttons */}
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleDownload}
                      disabled={isExporting}
                      className="flex-1 px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon icon="arrow-down-to-bracket" className="mr-2" />
                      {isExporting ? "Exporting..." : "Download"}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleShare}
                      disabled={isExporting}
                      className="flex-1 px-6 py-3 bg-white hover:bg-white/90 rounded-full text-gray-900 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon icon="arrow-up-from-bracket" className="mr-2" />
                      Share
                    </motion.button>
                  </div>

                  {/* Remove bookmark */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleRemoveBookmark}
                    className="px-6 py-2 text-white/50 hover:text-white/70 text-sm transition-colors"
                  >
                    Remove Bookmark
                  </motion.button>
                </div>
              </motion.div>

              {/* Hidden export card (full resolution 1080x1080) */}
              <div className="fixed -left-[9999px] -top-[9999px]">
                <div ref={exportCardRef}>
                  <BookmarkSnapshot
                    bookmarkData={selectedBookmark.bookmarkData}
                    size={1080}
                  />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}
